import React, { useEffect, useState } from 'react';

import ContextPanel from './ContextPanel';
import GraphPanel from './GraphPanel';
import PlanPanel from './PlanPanel';

type InsightsView = 'graph' | 'context' | 'plan';

const InsightsDock: React.FC = () => {
  const [activeView, setActiveView] = useState<InsightsView>('graph');

  useEffect(() => {
    const handleInsightsOpen = (rawEvent: Event) => {
      const event = rawEvent as CustomEvent<{ tab?: InsightsView }>;
      const tab = event.detail?.tab;
      if (tab === 'graph' || tab === 'context' || tab === 'plan') {
        setActiveView(tab);
      }
    };

    window.addEventListener('curious:insights-open', handleInsightsOpen as EventListener);
    return () => {
      window.removeEventListener('curious:insights-open', handleInsightsOpen as EventListener);
    };
  }, []);

  const tabConfig: { view: InsightsView; label: string; icon: React.ReactNode }[] = [
    {
      view: 'graph',
      label: 'Graph',
      icon: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="12" r="2.5" strokeWidth="2" />
          <circle cx="18" cy="7" r="2.5" strokeWidth="2" />
          <circle cx="18" cy="17" r="2.5" strokeWidth="2" />
          <line x1="11" y1="11" x2="16" y2="8" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="11" y1="13" x2="16" y2="16" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      view: 'context',
      label: 'Context',
      icon: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      view: 'plan',
      label: 'Plan',
      icon: (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
  ];

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-neutral-200 bg-neutral-50">
      <header className="border-b border-neutral-200 bg-white px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px] font-medium">
          {tabConfig.map(({ view, label, icon }) => (
            <button
              key={view}
              type="button"
              onClick={() => setActiveView(view)}
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 capitalize transition-all duration-150 ${
                activeView === view
                  ? 'border border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                  : 'border border-transparent bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700'
              }`}
            >
              {icon}
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden p-3">
        {activeView === 'graph' ? <GraphPanel /> : null}
        {activeView === 'context' ? <ContextPanel /> : null}
        {activeView === 'plan' ? <PlanPanel /> : null}
      </div>
    </aside>
  );
};

export default InsightsDock;
